import React from 'react';

import PrivateLayout from "layouts/private";
import { useParams } from 'react-router-dom';
import isEmpty from 'lodash.isempty';
import useToken from 'hooks/useToken';
import { useQuery } from 'react-query';
import { Button, Card, CardActions, CardContent, CardHeader, Container, Divider, Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Box } from '@mui/system';
import dateTime from 'utils/datetime';
import capitalize from 'lodash.capitalize';
import { fetchAccount } from 'apiRequest/account';
import { Link as RouterLink } from 'react-router-dom';
import { fetchCapitalsWithReportByAccount } from 'apiRequest/capitals';
import TableLoading from 'components/lSTable/TableLoading';
import currency from 'utils/currency';

export default function AccountShow() {
  const { id } = useParams();

  const { token } = useToken();

  const { data: account, isLoading, isError, error } = useQuery("account-fetchAccount", () => {
    return fetchAccount({
      token, id, params: {
        partials: [
          'capitals.figure',
        ]
      }
    });
  });

  const { data: capitals, isLoading: capitalIsLoading, isError: capitalIsError, error: capitalError } = useQuery("capital-fetchCapitalsWithReportByAccount", () => {
    return fetchCapitalsWithReportByAccount({
      token, id, params: {
        filters: {
          'account_id': id,
        }
      }
    });
  });

  return (
    <PrivateLayout loading={isLoading} error={error || capitalError} isError={isError || capitalIsError}>
      {!isEmpty(account?.data.data) && (
        <Container maxWidth="lg">
          <Card sx={{ padding: "10px" }}>
            <CardHeader
              title={`${capitalize(account?.data.data.name)} - ${account?.data.data.id}`}
              subheader={`${dateTime.format(account?.data.data.created_at)}`}
            />
            <Divider />
            <CardContent >
              <Stack direction="column" spacing={1.5}>
                <CardText label="Name" value={account?.data.data.name} />
                <Divider variant="middle" />
                <CardText label="Type" value={account?.data.data.type} />
                <Divider variant="middle" />
                <CardText label="Currency" value={account?.data.data.currency} />
                <Divider variant="middle" />
                <Box>
                  <Typography gutterBottom variant="caption" display="block">
                    Capitals
                  </Typography>
                  <CapitalTable data={capitals?.data.data} isLoading={capitalIsLoading} />
                </Box>
              </Stack>
            </CardContent>
            <CardActions>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  padding: "0 10px"
                }}
              >
                <Button
                  component={RouterLink}
                  to={`/accounts/${account?.data.data.id}/update`}
                  color="primary"
                  size="large"
                  variant="outlined"
                >
                  Update
                </Button>

              </Stack>
            </CardActions>
          </Card>
        </Container>
      )}
    </PrivateLayout >
  );
}

const CardText = ({ label, value }) => {
  return (
    <Box>
      <Typography gutterBottom variant="caption" display="block">
        {label}
      </Typography>
      <Typography variant="body1">
        {value}
      </Typography>
    </Box>
  );
};

const CapitalTable = ({ data, isLoading }) => {
  return (
    <TableContainer component={Card} sx={{ p: 2, position: "relative" }}>
      <TableLoading loading={isLoading} />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Currency</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Bought TRX Count</TableCell>
            <TableCell>Bought Quantity</TableCell>
            <TableCell>Bought Amount</TableCell>
            <TableCell>Sold TRX Count</TableCell>
            <TableCell>Sold Quantity</TableCell>
            <TableCell>Sold Amount</TableCell>
            <TableCell>Remaining Quantity</TableCell>
            <TableCell>Remaining Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!isLoading && data.map(({
            id,
            quantity,
            amount,
            currency_code,
            bought_transaction_count,
            bought_quantity,
            bought_amount,
            sold_transaction_count,
            sold_quantity,
            sold_amount
          }) => (
            <TableRow key={id}>
              <TableCell><Link component={RouterLink} to={`/capitals/${id}`}>{id}</Link></TableCell>
              <TableCell>{currency_code}</TableCell>
              <TableCell>{quantity}</TableCell>
              <TableCell>{currency.format(amount, 2)}</TableCell>
              <TableCell>{bought_transaction_count}</TableCell>
              <TableCell>{bought_quantity}</TableCell>
              <TableCell>{currency.format(bought_amount, 2)}</TableCell>
              <TableCell>{sold_transaction_count}</TableCell>
              <TableCell>{sold_quantity}</TableCell>
              <TableCell>{currency.format(sold_amount, 2)}</TableCell>
              <TableCell>{(quantity + bought_quantity) - sold_quantity}</TableCell>
              <TableCell>{currency.format((amount + bought_amount) - sold_amount,2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
